import { useEffect, useRef, type Dispatch, type SetStateAction } from "react";

import type {
  PaginationMeta,
  SortDirection,
} from "@/components/pagination/hooks/usePagination";
import usePersistedPagination from "@/components/pagination/hooks/usePersistedPagination";
import { useSearchPaginationQuery } from "@/components/pagination/hooks/useSearchPaginationQuery";
import { useGetParams } from "@/hooks/query/useGetParams";
import { useParamMapper } from "@/hooks/query/useParamMapper";

const EMPTY_PARAM_MAPPING: Record<string, string> = {};

interface UseTableQueryStateProps {
  filterKeys: string[];
  activeTab?: string;
  enabled?: boolean;
  limit?: number;
  paramMapping?: Record<string, string>;
  scope?: string;
  tableId?: string;
  transformBaseFilter?: (baseFilter: string) => string;
  defaultSort?: { sortBy?: string; orderBy?: SortDirection };
}

interface UseTableQueryStateReturn {
  paginationQuery: string;
  queryFilter: string;
  setLocalPagination: Dispatch<SetStateAction<PaginationMeta>>;
  resetToFirstPage: () => void;
  isPaginationLoading: boolean;
  shouldSkipQuery: boolean;
  /** Current per-tab persisted pagination, including sortBy and orderBy. */
  localPagination: PaginationMeta;
}

/**
 * Owns reusable table URL filter and local pagination query state.
 *
 * @param {UseTableQueryStateProps} props - Table filter configuration.
 * @returns {UseTableQueryStateReturn} Derived query inputs for paginated table requests.
 */
export const useTableQueryState = (
  props: UseTableQueryStateProps,
): UseTableQueryStateReturn => {
  const {
    activeTab,
    enabled = true,
    filterKeys,
    limit,
    paramMapping,
    scope,
    tableId,
    transformBaseFilter,
    defaultSort,
  } = props;

  const {
    localPagination,
    paginationQuery,
    resetToFirstPage,
    setLocalPagination,
    isLoading: isPaginationLoading,
  } = usePersistedPagination(limit, activeTab, tableId, {
    enabled,
    defaultSort,
  });
  const baseFilter = useGetParams(filterKeys);
  const { mappedQueryString } = useParamMapper({
    paramMapping: paramMapping ?? EMPTY_PARAM_MAPPING,
    scope,
  });

  const resolvedBaseFilter = transformBaseFilter
    ? transformBaseFilter(baseFilter)
    : baseFilter;
  const composedFilter = `${resolvedBaseFilter}${mappedQueryString}`;
  const previousFilterRef = useRef(composedFilter);
  const previousTabRef = useRef(activeTab);

  useEffect(() => {
    if (!enabled) {
      return;
    }

    if (previousTabRef.current !== activeTab) {
      previousTabRef.current = activeTab;
      previousFilterRef.current = composedFilter;
      return;
    }

    if (previousFilterRef.current === composedFilter) {
      return;
    }

    previousFilterRef.current = composedFilter;
    resetToFirstPage();
  }, [activeTab, composedFilter, enabled, resetToFirstPage]);

  const { queryFilter } = useSearchPaginationQuery(
    composedFilter,
    paginationQuery,
    {
      filterParams: filterKeys,
    },
  );
  const hasPaginationQuery = paginationQuery.includes("limit=");

  return {
    paginationQuery: enabled ? paginationQuery : "",
    queryFilter,
    setLocalPagination,
    resetToFirstPage,
    isPaginationLoading,
    shouldSkipQuery: enabled && (isPaginationLoading || !hasPaginationQuery),
    localPagination,
  };
};
